import { MODES } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import {
    getAnchorWithVector,
    getRotateVector,
    getVectorWithAnchor,
} from '@src/slide/application/service/snap-line-service/util/vector';
import {
    MovementDirection,
    OperationAnchor,
    OrientDirection,
    Orient,
} from '@src/slide/application/service/snap-line-service/interface/orient.interface';
import type { IPoint } from '@editor/src/page/index/components/canvas/resizable.interface';
import type { DragEvent, MouseEvent } from 'react';

/**
 * 判定鼠标的移动趋势
 */
export function predictMovingDirection(event: DragEvent): MovementDirection {
    const { movementX, movementY } = event;

    if (movementX > 0) {
        if (movementY > 0) {
            return MovementDirection.RIGHT_BOTTOM;
        } if (movementY < 0) {
            return MovementDirection.RIGHT_TOP;
        }
        return MovementDirection.RIGHT;
    }

    if (movementX < 0) {
        if (movementY > 0) {
            return MovementDirection.LEFT_BOTTOM;
        } if (movementY < 0) {
            return MovementDirection.LEFT_TOP;
        }
        return MovementDirection.LEFT;
    }

    if (movementY > 0) {
        return MovementDirection.BOTTOM;
    } if (movementY < 0) {
        return MovementDirection.TOP;
    }
    return MovementDirection.NONE;
}


/**
 * 获取与移动方向相匹配的 Orient
 */
export function getMovingMatchedOrient(mouseEvent: MouseEvent) {
    const { movementX, movementY } = mouseEvent;
    if ((Math.abs(movementX) - Math.abs(movementY)) > 0) {
        return Orient.HORIZONTAL;
    }

    return Orient.VERTICAL;
}

/**
 * 获取与移动方向相的基础方向，不包含斜角
 */
export function getMovingOrientDirection(mouseEvent: MouseEvent) {
    const { movementX, movementY } = mouseEvent;
    if ((Math.abs(movementX) - Math.abs(movementY)) > 0) {
        return movementX >= 0 ? OrientDirection.RIGHT : OrientDirection.LEFT;
    }

    return movementY >= 0 ? OrientDirection.BOTTOM : OrientDirection.TOP;
}

/**
 * 判断移动方向与 Orient 是否相冲突
 */
export function isMovingDirectionMatchedWithOrient(orient: Orient, direction: MovementDirection): boolean {
    return getOrientMatchedDirections(orient).includes(direction);
}

/**
 * 获取与 Orient 相匹配的移动方向
 */
export function getOrientMatchedDirections(orient: Orient): MovementDirection[] {
    if (orient === Orient.VERTICAL) {
        return [
            MovementDirection.LEFT, MovementDirection.LEFT_TOP, MovementDirection.LEFT_BOTTOM,
            MovementDirection.RIGHT, MovementDirection.RIGHT_TOP, MovementDirection.RIGHT_BOTTOM,
            MovementDirection.NONE,
        ];
    }

    return [
        MovementDirection.TOP, MovementDirection.LEFT_TOP, MovementDirection.RIGHT_TOP,
        MovementDirection.BOTTOM, MovementDirection.LEFT_BOTTOM, MovementDirection.RIGHT_BOTTOM,
        MovementDirection.NONE,
    ];
}

/**
 * MODES 与 OperationAnchor 的映射
 */
const anchorModeMapping: Record<MODES, OperationAnchor> = {
    [MODES.TOP]: OperationAnchor.TOP,
    [MODES.BOTTOM]: OperationAnchor.BOTTOM,
    [MODES.RIGHT]: OperationAnchor.RIGHT,
    [MODES.LEFT]: OperationAnchor.LEFT,
    [MODES.TOP_LEFT]: OperationAnchor.LEFT_TOP,
    [MODES.TOP_RIGHT]: OperationAnchor.RIGHT_TOP,
    [MODES.BOTTOM_LEFT]: OperationAnchor.LEFT_BOTTOM,
    [MODES.BOTTOM_RIGHT]: OperationAnchor.RIGHT_BOTTOM,
    [MODES.MOVE]: OperationAnchor.ALL,
};

/**
 * 通过MODES 获取 OperationAnchor
 */
export function getOperationAnchorWithMode(mode: MODES): OperationAnchor {
    return anchorModeMapping[mode];
}

const anchorFlipVerticalMapping = {
    [OperationAnchor.TOP]: OperationAnchor.BOTTOM,
    [OperationAnchor.BOTTOM]: OperationAnchor.TOP,
    [OperationAnchor.LEFT_TOP]: OperationAnchor.LEFT_BOTTOM,
    [OperationAnchor.LEFT_BOTTOM]: OperationAnchor.LEFT_TOP,
    [OperationAnchor.RIGHT_TOP]: OperationAnchor.RIGHT_BOTTOM,
    [OperationAnchor.RIGHT_BOTTOM]: OperationAnchor.RIGHT_TOP,
};
const anchorFlipHorizontalMapping = {
    [OperationAnchor.LEFT]: OperationAnchor.RIGHT,
    [OperationAnchor.RIGHT]: OperationAnchor.LEFT,
    [OperationAnchor.LEFT_TOP]: OperationAnchor.RIGHT_TOP,
    [OperationAnchor.LEFT_BOTTOM]: OperationAnchor.RIGHT_BOTTOM,
    [OperationAnchor.RIGHT_TOP]: OperationAnchor.LEFT_TOP,
    [OperationAnchor.RIGHT_BOTTOM]: OperationAnchor.LEFT_BOTTOM,
};

/**
 * 根据角度和旋转之前的锚点，计算旋转之后的锚点
 */
export function getRotatedAnchor(anchor: OperationAnchor, rotate: number) {
    const vector = getVectorWithAnchor(anchor);
    const rotateVector = getRotateVector(vector, rotate);
    return getAnchorWithVector(rotateVector);
}

/**
 * 获取翻转后的判定锚点
 */
export function getFlippedAnchor(originAnchor: OperationAnchor, flipHorizontal = false, flipVertical = false) {
    let anchor = originAnchor;

    if (flipVertical) {
        anchor = anchorFlipVerticalMapping[anchor] || anchor;
    }
    if (flipHorizontal) {
        anchor = anchorFlipHorizontalMapping[anchor] || anchor;
    }

    return anchor;
}

export function getOppositeOrient(orient: Orient) {
    return orient === Orient.VERTICAL ? Orient.HORIZONTAL : Orient.VERTICAL;
}

/**
 * 获取鼠标位置
 */
export function getMousePoint(e: any): IPoint {
    return {
        x: e.touches ? e.touches[0].clientX : e.clientX,
        y: e.touches ? e.touches[0].clientY : e.clientY,
    };
}

export function isCornerAnchor(anchor: OperationAnchor) {
    return anchor ===  OperationAnchor.LEFT_BOTTOM
        || anchor === OperationAnchor.LEFT_TOP
        || anchor === OperationAnchor.RIGHT_TOP
        || anchor === OperationAnchor.RIGHT_BOTTOM;
}
